(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-itmart24-exit-intent]';
  var SESSION_KEY = 'itmart24_exit_intent_state';
  var MIN_DESKTOP_ELAPSED_MS = 4000;
  var MOBILE_DELAY_MS = 30000;
  var MOBILE_SCROLL_THRESHOLD = 0.4;
  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function isDesignMode() {
    return Boolean(window.Shopify && Shopify.designMode);
  }

  function hasShownThisSession() {
    try {
      return Boolean(window.sessionStorage.getItem(SESSION_KEY));
    } catch (_error) {
      return false;
    }
  }

  function setSessionState(value) {
    try {
      window.sessionStorage.setItem(SESSION_KEY, value);
    } catch (_error) {}
  }

  function isMobileLikeViewport() {
    return window.matchMedia('(pointer: coarse), (max-width: 989px)').matches;
  }

  function isExcludedRoute() {
    var path = String(window.location.pathname || '').toLowerCase();

    if (window.Shopify && window.Shopify.Checkout) {
      return true;
    }

    return /^\/(?:cart|checkout|account)(?:\/|$)/.test(path)
      || path.indexOf('/challenge') === 0
      || path.indexOf('/payments') === 0;
  }

  function getReviewBase(root) {
    var sessionRoot = document.querySelector('[data-user-portal-session-root]');
    var configured = sessionRoot ? sessionRoot.getAttribute('data-user-portal-base') : '';
    return String(configured || root.getAttribute('data-review-base') || 'https://user.itmart24.com').replace(/\/$/, '');
  }

  function getReviewUrl(root) {
    var target = getReviewBase(root);
    var url = new URL(target + '/');
    url.searchParams.set('source', 'exit-intent-popup');
    url.searchParams.set('origin', window.location.hostname || 'itmart24.com');
    url.searchParams.set('intent', 'write-first-review');
    return url.toString();
  }

  function getExploreUrl(root) {
    var target = root.getAttribute('data-explore-url') || '/collections/all';

    try {
      var url = new URL(target, window.location.origin);
      url.searchParams.set('source', 'exit-intent-popup');
      return url.toString();
    } catch (_error) {
      return target;
    }
  }

  function getFocusableNodes(root) {
    return Array.prototype.slice.call(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (node) {
      return !node.hasAttribute('hidden') && node.offsetParent !== null;
    });
  }

  function init() {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root || isDesignMode() || isExcludedRoute() || hasShownThisSession()) {
      return;
    }

    var dialog = root.querySelector('.itmart24-exit-intent__dialog');
    var primaryAction = root.querySelector('[data-exit-intent-primary]');
    var secondaryAction = root.querySelector('[data-exit-intent-secondary]');
    var pageEnteredAt = Date.now();
    var hasOpened = false;
    var previousActiveElement = null;
    var mobileTimerId = null;
    var scrollListenerOptions = { passive: true };

    if (!dialog) {
      return;
    }

    if (primaryAction) {
      primaryAction.setAttribute('href', getReviewUrl(root));
    }

    if (secondaryAction) {
      secondaryAction.setAttribute('href', getExploreUrl(root));
    }

    function cleanup() {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousemove', onDesktopPointerMove, true);
      window.removeEventListener('scroll', onMobileScroll, scrollListenerOptions);
      window.removeEventListener('resize', onResize, true);

      if (mobileTimerId) {
        window.clearTimeout(mobileTimerId);
        mobileTimerId = null;
      }
    }

    function closePopup(reason) {
      if (!hasOpened) {
        return;
      }

      hasOpened = false;
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('itmart24-exit-intent-open');
      setSessionState(reason || 'dismissed');

      if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
        previousActiveElement.focus({ preventScroll: true });
      }

      cleanup();
    }

    function trapFocus(event) {
      if (event.key !== 'Tab') {
        return;
      }

      var focusableNodes = getFocusableNodes(root);
      if (!focusableNodes.length) {
        event.preventDefault();
        dialog.focus({ preventScroll: true });
        return;
      }

      var first = focusableNodes[0];
      var last = focusableNodes[focusableNodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function onKeyDown(event) {
      if (!hasOpened) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closePopup('dismissed');
        return;
      }

      trapFocus(event);
    }

    function openPopup(reason) {
      if (hasOpened || hasShownThisSession() || isExcludedRoute()) {
        return;
      }

      hasOpened = true;
      previousActiveElement = document.activeElement;
      setSessionState(reason || 'shown');
      root.hidden = false;
      root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('itmart24-exit-intent-open');
      document.addEventListener('keydown', onKeyDown, true);
      cleanupTriggerListeners();

      window.requestAnimationFrame(function () {
        var focusTarget = root.querySelector('.itmart24-exit-intent__close') || primaryAction || dialog;
        if (focusTarget && typeof focusTarget.focus === 'function') {
          focusTarget.focus({ preventScroll: true });
        }
      });
    }

    function isCloseTarget(target) {
      return Boolean(target && target.closest && target.closest('[data-exit-intent-close]'));
    }

    function onCloseInteraction(event) {
      if (!hasOpened) {
        return;
      }

      if (!isCloseTarget(event.target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      closePopup('dismissed');
    }

    root.addEventListener('click', onCloseInteraction, true);
    root.addEventListener('touchend', onCloseInteraction, { capture: true, passive: false });

    if (primaryAction) {
      primaryAction.addEventListener('click', function () {
        setSessionState('primary');
      });
    }

    if (secondaryAction) {
      secondaryAction.addEventListener('click', function () {
        setSessionState('secondary');
      });
    }

    dialog.addEventListener('click', function (event) {
      event.stopPropagation();
    });

    dialog.addEventListener('touchend', function (event) {
      if (!isCloseTarget(event.target)) {
        event.stopPropagation();
      }
    }, { passive: true });

    function onDesktopPointerMove(event) {
      if (isMobileLikeViewport() || hasOpened || hasShownThisSession()) {
        return;
      }

      if (Date.now() - pageEnteredAt < MIN_DESKTOP_ELAPSED_MS) {
        return;
      }

      if (event.clientY <= 12 && event.relatedTarget === null) {
        openPopup('desktop-exit-intent');
      }
    }

    function getScrollProgress() {
      var doc = document.documentElement;
      var scrollableHeight = Math.max(doc.scrollHeight - window.innerHeight, 0);
      if (!scrollableHeight) {
        return 0;
      }

      return window.scrollY / scrollableHeight;
    }

    function onMobileScroll() {
      if (!isMobileLikeViewport() || hasOpened || hasShownThisSession()) {
        return;
      }

      if (getScrollProgress() >= MOBILE_SCROLL_THRESHOLD) {
        openPopup('mobile-scroll');
      }
    }

    function scheduleMobileTimer() {
      if (!isMobileLikeViewport() || hasOpened || hasShownThisSession() || mobileTimerId) {
        return;
      }

      mobileTimerId = window.setTimeout(function () {
        mobileTimerId = null;

        if (!hasOpened && !hasShownThisSession() && isMobileLikeViewport()) {
          openPopup('mobile-timer');
        }
      }, MOBILE_DELAY_MS);
    }

    function cleanupTriggerListeners() {
      document.removeEventListener('mousemove', onDesktopPointerMove, true);
      window.removeEventListener('scroll', onMobileScroll, scrollListenerOptions);

      if (mobileTimerId) {
        window.clearTimeout(mobileTimerId);
        mobileTimerId = null;
      }
    }

    function bindTriggerListeners() {
      if (isMobileLikeViewport()) {
        window.addEventListener('scroll', onMobileScroll, scrollListenerOptions);
        scheduleMobileTimer();
        return;
      }

      document.addEventListener('mousemove', onDesktopPointerMove, true);
    }

    function onResize() {
      if (hasOpened || hasShownThisSession()) {
        return;
      }

      cleanupTriggerListeners();
      bindTriggerListeners();
    }

    bindTriggerListeners();
    window.addEventListener('resize', onResize, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
